import { Component, OnInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Piece } from 'app/models/piece';
import { PiecesService } from 'app/services/pieces/pieces.service';
import { ProjetsService } from 'app/services/projets/projets.service';
import { Location } from '@angular/common';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import Swal from 'sweetalert2';

interface FormattingCommand {
  tag: string;
  display: string;
}

@Component({
  selector: 'app-modifier-piece',
  templateUrl: './modifier-piece.component.html',
  styleUrls: ['./modifier-piece.component.scss']
})
export class ModifierPieceComponent implements OnInit, OnDestroy {
  @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;
  @ViewChild('pieceImagesInput', { static: false }) pieceImagesInput!: ElementRef;

  // Data properties
  piece: Piece = new Piece();
  agenciesProjet: any[] = [];
  uploadedImages: string[] = [];

  // State properties
  isLoading: boolean = false;
  isUploadingImages: boolean = false;
  private destroy$ = new Subject<void>();

  // Constants
  private readonly FORMATTING_COMMANDS: { [key: string]: FormattingCommand } = {
    bold: { tag: 'strong', display: 'B' },
    italic: { tag: 'em', display: 'I' },
    underline: { tag: 'u', display: 'U' },
    list: { tag: 'ul', display: 'list' }
  };

  constructor(
    private projetService: ProjetsService,
    private piecesService: PiecesService,
    private location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private awsService: AwscontrolService
  ) {}

  ngOnInit(): void {
    this.initializeData();
    this.subscribeToRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============= INITIALIZATION =============
  
  private initializeData(): void {
    this.loadProjects();
    this.loadPieceData();
  }

  private subscribeToRefresh(): void {
    this.projetService.refreshNedeed
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadPieceData());
  }

  private loadProjects(): void {
    this.projetService.getListProjet()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (Array.isArray(data)) {
            this.agenciesProjet = data;
          } else if (data) {
            this.agenciesProjet = [data];
          } else {
            this.agenciesProjet = [];
          }
        },
        error: (err) => this.handleError('Erreur lors du chargement des projets', err)
      });
  }

  private loadPieceData(): void {
    const pieceId = this.activatedRoute.snapshot.paramMap.get('id');
    if (!pieceId) {
      this.showError('ID de l\'appartement introuvable');
      return;
    }

    this.isLoading = true;
    this.piecesService.getPiece(pieceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.piece = data;
          this.initializeImages();
          this.isLoading = false;
        },
        error: (err) => {
          this.handleError('Erreur lors du chargement de l\'appartement', err);
          this.isLoading = false;
        }
      });
  }

  private initializeImages(): void {
    if (this.piece.photos && Array.isArray(this.piece.photos)) {
      this.uploadedImages = [...this.piece.photos];
    } else {
      this.uploadedImages = [];
    }
  }

  // ============= TEXT FORMATTING =============

  formatText(command: string): void {
    if (!this.descriptionTextarea || !this.piece.description) {
      this.showWarning('Veuillez d\'abord saisir du texte');
      return;
    }

    const textarea = this.descriptionTextarea.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.piece.description.substring(start, end);

    if (!selectedText) {
      this.showWarning('Veuillez sélectionner du texte à formater');
      return;
    }

    const formattedText = this.applyFormatting(command, selectedText);
    this.piece.description = 
      this.piece.description.substring(0, start) + 
      formattedText + 
      this.piece.description.substring(end);

    this.focusTextarea(textarea, start + formattedText.length);
  }

  private applyFormatting(command: string, text: string): string {
    const cmd = this.FORMATTING_COMMANDS[command];
    if (!cmd) return text;

    if (command === 'list') {
      return `<ul><li>${text}</li></ul>`;
    }
    return `<${cmd.tag}>${text}</${cmd.tag}>`;
  }

  private focusTextarea(textarea: HTMLTextAreaElement, position: number): void {
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(position, position);
    }, 0);
  }

  // ============= IMAGE MANAGEMENT =============

  openImagesSelector(): void {
    if (this.pieceImagesInput && this.pieceImagesInput.nativeElement) {
      this.pieceImagesInput.nativeElement.click();
    }
  }

  async selectFileimg(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    await this.uploadMultipleImages(files);
    
    // Reset input
    input.value = '';
  }

  private async uploadMultipleImages(files: File[]): Promise<void> {
    this.isUploadingImages = true;
    
    try {
      for (const file of files) {
        await this.uploadSingleImage(file);
      }
    } finally {
      this.isUploadingImages = false;
    }
  }

  private async uploadSingleImage(file: File): Promise<void> {
    const toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });

    toast.fire({
      icon: 'info',
      title: `Téléchargement de ${file.name}...`
    });

    try {
      const folder = 'pieces';
      const response = await this.awsService.uploadFile(file, folder).toPromise();

      if (response.success && response.urls && response.urls.length > 0) {
        const uploadedUrl = response.urls[0];
        this.uploadedImages.push(uploadedUrl);
        
        toast.fire({
          icon: 'success',
          title: `${file.name} téléchargé avec succès`
        });
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      
      toast.fire({
        icon: 'error',
        title: `Échec du téléchargement de ${file.name}`
      });
    }
  }

  async removeImage(index: number): Promise<void> {
    const imageUrl = this.uploadedImages[index];
    
    const result = await Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer cette image?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await this.awsService.deleteFile(imageUrl).toPromise();
        
        this.uploadedImages.splice(index, 1);
        this.showSuccess('Image supprimée', 'L\'image a été supprimée avec succès');
      } catch (error) {
        console.error('Error deleting image from S3:', error);
        
        this.uploadedImages.splice(index, 1);
        this.showSuccess('Image supprimée', 'L\'image a été supprimée de la liste');
      }
    }
  }

  moveImageUp(index: number): void {
    if (index > 0) {
      this.swapImages(index, index - 1);
    }
  }

  moveImageDown(index: number): void {
    if (index < this.uploadedImages.length - 1) {
      this.swapImages(index, index + 1);
    }
  }

  private swapImages(index1: number, index2: number): void {
    [this.uploadedImages[index1], this.uploadedImages[index2]] = 
    [this.uploadedImages[index2], this.uploadedImages[index1]];
  }

  // ============= FORM VALIDATION (NO REQUIRED FIELDS) =============

  private validateForm(): boolean {
    if (this.isUploadingImages) {
      this.showWarning('Veuillez patienter', 'Des images sont en cours de téléchargement');
      return false;
    }
    return true;
  }

  // ============= FORM SUBMISSION =============

  async updatePiece(): Promise<void> {
    if (this.isLoading) {
      this.showWarning('Veuillez patienter', 'Chargement en cours');
      return;
    }

    if (this.isUploadingImages) {
      this.showWarning('Veuillez patienter', 'Des images sont en cours de téléchargement');
      return;
    }

    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;
    this.showLoadingDialog();

    const pieceId = this.activatedRoute.snapshot.paramMap.get('id');
    const updatedPiece = this.buildPiecePayload();

    this.piecesService.updatePiece(pieceId, updatedPiece)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.handleUpdateSuccess(),
        error: (err) => this.handleUpdateError(err)
      });
  }

  private buildPiecePayload(): any {
    return {
      name: this.piece.name || '',
      status: this.piece.status || 'DISPONIBLE',
      projetName: this.piece.projetName || '',
      category: this.piece.category || '',
      type: this.piece.type || '',
      description: this.piece.description || '',
      prixMdt: this.piece.prixMdt || '0',
      surface: this.piece.surface?.trim() ? this.piece.surface : null,
      photos: this.uploadedImages,
      videoUrl: this.piece.videoUrl?.trim() ? this.piece.videoUrl : null
    };
  }

  private handleUpdateSuccess(): void {
    Swal.close();
    this.isLoading = false;

    Swal.fire({
      icon: 'success',
      title: 'Succès',
      text: 'Appartement modifié avec succès!',
      timer: 2000,
      showConfirmButton: false
    }).then(() => {
      setTimeout(() => this.loadPieceData(), 500);
      this.goBack();
    });
  }

  private handleUpdateError(error: any): void {
    Swal.close();
    this.isLoading = false;
    console.error('Error updating piece:', error);
    
    let errorMessage = 'Échec de la modification de l\'appartement';
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    this.showError('Erreur', errorMessage);
  }

  // ============= NAVIGATION =============

  annule(): void {
    this.goBack();
  }

  private goBack(): void {
    if (this.location) {
      this.location.back();
    } else {
      this.router.navigate(['/']);
    }
  }

  // ============= NOTIFICATIONS =============

  private showLoadingDialog(): void {
    Swal.fire({
      title: 'Mise à jour en cours...',
      html: 'Modification de votre appartement',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });
  }

  private showSuccess(title: string, text?: string): void {
    Swal.fire({
      icon: 'success',
      title,
      text,
      timer: 2000,
      showConfirmButton: false
    });
  }

  private showWarning(title: string, text?: string): void {
    Swal.fire({
      icon: 'warning',
      title,
      text,
      confirmButtonColor: '#3f51b5'
    });
  }

  private showError(title: string, text?: string): void {
    Swal.fire({
      icon: 'error',
      title,
      text,
      confirmButtonColor: '#3f51b5'
    });
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.showError(message);
  }

  // ============= ADDITIONAL AWS HELPER METHODS =============

  async checkAWSServiceHealth(): Promise<void> {
    try {
      const health = await this.awsService.checkHealth().toPromise();
      Swal.fire({
        icon: 'success',
        title: 'Service AWS Connecté',
        html: `
          <div style="text-align: left;">
            <p><strong>Statut:</strong> ${health.status}</p>
            <p><strong>Bucket:</strong> ${health.bucket}</p>
            <p><strong>Région:</strong> ${health.region}</p>
            <p><strong>Dernière vérification:</strong> ${health.timestamp}</p>
          </div>
        `,
        confirmButtonColor: '#3f51b5'
      });
    } catch (error) {
      this.showError('Service AWS non disponible', 'Impossible de vérifier la connexion au service AWS');
    }
  }

  getFileNameFromUrl(url: string): string {
    return this.awsService.extractFileName(url);
  }
}